import dayjs from 'dayjs';

import { type RevenueChartGroupBy } from '@/utils/date';

import type { RevenueChartQuery } from '../../../../../../../../../../generated/graphql';

type AnalyticsResponse = RevenueChartQuery['dashboardWidgetsRevenueAnalyticsV2'];

export type RevenueChartCategory = 'revenue' | 'average_bill' | 'return' | 'amount';

export type RevenueChartCategoryData = {
  main_value: number;
  percent_change: number;
  y_axis_divisions: number[];
  current_period_start: string;
  current_period_end: string;
  comparison_period_start: string | null;
  comparison_period_end: string | null;
  comparison_period_label: string | null;
  data: Array<{
    date: string;
    value: number;
    comparison_value: number | null;
    comparison_date: string | null;
  }>;
};

export type RevenueChartData = Record<RevenueChartCategory, RevenueChartCategoryData>;

const CATEGORY_SUMMARY_KEY = {
  revenue: 'revenue',
  average_bill: 'average_bill',
  return: 'refund',
  amount: 'amount',
} as const satisfies Record<RevenueChartCategory, keyof AnalyticsResponse['summary']>;

const CATEGORY_METRIC_KEY = {
  revenue: 'revenue',
  average_bill: 'average_bill',
  return: 'refund',
  amount: 'amount',
} as const satisfies Record<RevenueChartCategory, keyof AnalyticsResponse['chart_data'][number]['current_period']>;

const roundUpToNearestRound = (value: number): number => {
  if (value === 0) {
    return 1000;
  }

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;

  return Math.ceil(normalized) * magnitude;
};

const calculateYAxisDivisions = (maxValue: number): number[] => {
  const roundedMax = roundUpToNearestRound(maxValue);
  const step = roundedMax / 4;

  return [0, step, step * 2, step * 3, roundedMax];
};

export const mapRevenueAnalyticsToChartData = (response: AnalyticsResponse): RevenueChartData => {
  const currentPeriodStart = response.chart_data.at(0)?.current_period.date ?? '';
  const currentPeriodEnd = response.chart_data.at(-1)?.current_period.date ?? '';
  const comparisonPeriodStart = response.chart_data.at(0)?.previous_period.date ?? null;
  const comparisonPeriodEnd = response.chart_data.at(-1)?.previous_period.date ?? null;

  return (Object.keys(CATEGORY_SUMMARY_KEY) as RevenueChartCategory[]).reduce<RevenueChartData>((result, category) => {
    const summaryKey = CATEGORY_SUMMARY_KEY[category];
    const metricKey = CATEGORY_METRIC_KEY[category];
    const summary = response.summary[summaryKey];
    const comparisonPeriodLabel = summary.comparison_period_label ?? null;
    const hasComparison = Boolean(comparisonPeriodLabel);

    let maxValue = 0;

    const data = response.chart_data.map(({ current_period, previous_period }) => {
      const value = Number(current_period[metricKey] ?? 0);
      const comparisonValue = hasComparison ? Number(previous_period[metricKey] ?? 0) : null;

      maxValue = Math.max(maxValue, value, comparisonValue ?? 0);

      return {
        date: current_period.date,
        value,
        comparison_value: comparisonValue,
        comparison_date: hasComparison ? previous_period.date : null,
      };
    });

    result[category] = {
      main_value: Number(summary.current_total ?? 0),
      percent_change: Number(summary.diff_percent ?? 0),
      y_axis_divisions: calculateYAxisDivisions(maxValue),
      current_period_start: currentPeriodStart,
      current_period_end: currentPeriodEnd,
      comparison_period_start: comparisonPeriodStart,
      comparison_period_end: comparisonPeriodEnd,
      comparison_period_label: comparisonPeriodLabel,
      data,
    };

    return result;
  }, {} as RevenueChartData);
};

export const getRevenueAnalyticsGroupBy = (dateFrom: Date, dateTo: Date): RevenueChartGroupBy => {
  const dayCount = dayjs(dateTo).diff(dayjs(dateFrom).startOf('day'), 'day') + 1;

  return dayCount <= 14 ? 'hour' : 'day';
};
